import { useUserInfo } from '../../shared/UserInfo';
import { useEffect, useState } from 'react';
import { callUserInfoAPI, getSepPlans } from '../../shared/APIHelper';
import { SEP_PLANS_API_ERR_MSG } from '../../shared/AppConstants';
import { Card, CardContent } from '@mui/material';
import { SepPlansDatagrid } from './SepPlansDatagrid';
import {
  setSepPlanCount,
  setSepPlanError,
  setSepPlanLoading
} from '../../shared/slices/sepPlansSlice';
import { useAppDispatch } from '../../shared/hooks/reduxHooks';

export const SepPlansTableProvider = ({ commonProps, masterScopeJson }) => {
  const {
    strategyNumber,
    incrementAPIInProgress,
    decrementAPIInProgress,
    apiInProgressCount,
    setStatusErrorMsg
  } = commonProps;
  const info = useUserInfo();
  const tokenAvailable = info?.token;
  const dispatch = useAppDispatch();
  const [plans, setPlans] = useState([]);
  const [error, setError] = useState(false);
  const [userInfoMap, setUserInfoMap] = useState(new Map());

  async function fetchSepPlans() {
    incrementAPIInProgress();
    dispatch(setSepPlanLoading(true));

    const unitsMap = new Map(Object.entries(masterScopeJson?.unitsMap));

    try {
      const plans = await getSepPlans(strategyNumber);

      setError(false);
      dispatch(setSepPlanError(false));

      return plans.map((plan) => {
        const unitName = unitsMap?.get(plan.programUnit)?.name;

        return {
          ...plan,
          id: plan.planId,
          programUnitName: unitName
            ? `${unitName} - ${plan.programUnit}`
            : plan.programUnit
        };
      });
    } catch (error) {
      setStatusErrorMsg(SEP_PLANS_API_ERR_MSG, error);
      setError(true);
      dispatch(setSepPlanError(true));
      return [];
    } finally {
      decrementAPIInProgress();
      dispatch(setSepPlanLoading(false));
    }
  }

  async function fetchData() {
    let sepPlans = await fetchSepPlans();

    setPlans(sepPlans);
    dispatch(setSepPlanCount(sepPlans.length));

    if (sepPlans?.length > 0) {
      const userIds = [...new Set(sepPlans.flatMap((plan) => plan.sciOwners))];

      callUserInfoAPI(
        userIds,
        setUserInfoMap,
        null,
        incrementAPIInProgress,
        decrementAPIInProgress,
        setStatusErrorMsg,
        false
      );
    }
  }

  useEffect(() => {
    if (tokenAvailable && masterScopeJson?.unitsMap) fetchData();
  }, [tokenAvailable, masterScopeJson?.unitsMap]);

  useEffect(() => {
    if (userInfoMap?.size > 0) {
      setPlans((prevPlans) =>
        prevPlans.map((plan) => {
          const sciOwnersFullNames = plan.sciOwners.map((ownerId) => {
            const ownerInfo = userInfoMap.get(ownerId);
            return ownerInfo
              ? `${ownerInfo.firstName} ${ownerInfo.lastName} - ${ownerId}`
              : ownerId;
          });

          return {
            ...plan,
            sciBuyerFullName: sciOwnersFullNames.join(', ')
          };
        })
      );
    }
  }, [userInfoMap]);

  function paintResponse() {
    if (apiInProgressCount.count > 0 || error) {
      return <></>;
    }

    return (
      plans.length > 0 && (
        <Card sx={(theme) => ({ mt: theme.fuel6Tokens.JddcSpacingMedium })}>
          <CardContent>
            <SepPlansDatagrid rows={plans} />
          </CardContent>
        </Card>
      )
    );
  }

  return paintResponse();
};
